import { useRef } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogProps,
  Divider,
  LinearProgress,
  Typography,
  useTheme,
} from '@mui/material'

import Close from 'assets/icons/Close.png'

import {
  CloseIcon,
  LCDialogContent,
  LCDialogLink,
  LCDialogText,
  LCDialogTitle,
} from './file-upload.styles'

type FileUploadProps = {
  handleClose: () => void
  onFileUpload: (event: any) => void
  isLoading: boolean
  error: string
  /** Dialog title; defaults to load confirmation for the create-order flow. */
  title?: string
  /** When true (e.g. sidebar Create order), show “Create manually” below the upload link. */
  showCreateManually?: boolean
  onCreateManually?: () => void
} & DialogProps

const FileUpload = ({
  open,
  handleClose,
  onFileUpload,
  isLoading,
  error,
  title = 'Upload load confirmation',
  showCreateManually = false,
  onCreateManually,
}: FileUploadProps) => {
  const theme = useTheme()
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const handleClick = () => {
    fileInputRef?.current?.click()
  }

  return (
    <Dialog
      open={open}
      PaperProps={{
        sx: {
          minWidth: '400px',
        },
      }}
    >
      <LCDialogContent
        spacing={0}
        sx={showCreateManually ? { height: 'auto', minHeight: 200, pb: 2 } : undefined}
      >
        <CloseIcon src={Close} alt="close icon" onClick={handleClose} />
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          width="100%"
          textAlign="center"
        >
          <LCDialogTitle>{title}</LCDialogTitle>
          <LCDialogText>
            <LCDialogLink
              style={{ color: theme.palette.primary.main }}
              onClick={handleClick}
            >
              Click here to browse files
              <input
                type="file"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={(event) => {
                  const input = event.target
                  void Promise.resolve(onFileUpload(event)).finally(() => {
                    input.value = ''
                  })
                }}
              />
            </LCDialogLink>
          </LCDialogText>
          {isLoading && (
            <Box mt={2}>
              <LinearProgress />
            </Box>
          )}
          {error !== '' && (
            <Box mt={2}>
              <Typography color="error.main">{error}</Typography>
            </Box>
          )}
          {showCreateManually && onCreateManually && (
            <>
              <Divider sx={{ width: '100%', my: 2 }} />
              <Button
                variant="outlined"
                fullWidth
                sx={{ textTransform: 'none', maxWidth: 360 }}
                onClick={onCreateManually}
              >
                Create manually
              </Button>
            </>
          )}
        </Box>
      </LCDialogContent>
    </Dialog>
  )
}

export default FileUpload